import type {
  AlertConfig,
  AlertCreateBody,
  AlertDirection,
  AlertEvent,
  AlertRecipient,
  AlertRecipientCreateBody,
  AlertsStatusResponse,
  BentoniteRecord,
  DiaryEntry,
  DriveInfo,
  InclineConfig,
  DrawingInfo,
  DrawingAutoAnalyzeRequest,
  DrawingAutoAnalyzeResponse,
  HealthResponse,
  JfConfigBody,
  JfConfigResponse,
  JfPredictionsResponse,
  LatestResponse,
  LubricationLatest,
  ParameterId,
  PerChainageRow,
  PipeChange,
  PipeSchedule,
  Polyline,
  PreprocessingParams,
  RamStageCorrection,
  SavedPreprocessing,
  PumpId,
  PumpMetric,
  SecondaryAnalysis,
  SecondaryResponse,
  SeriesResponse,
  SlurryChannelId,
  SlurryManualCreateBody,
  SlurryManualReading,
  SlurrySeriesResponse,
  SlurrySummaryResponse,
} from "@tbm/shared";

export interface PumpAvailabilityResponse {
  pumps: Array<{ id: PumpId; displayName: string; hasData: boolean }>;
}

export interface AdminAnalytics {
  days: number;
  totals: {
    events: number;
    pageViews: number;
    distinctUsers: number;
    distinctIps: number;
    logins: number;
  };
  dailyActiveUsers: Array<{ day: string; users: number; events: number }>;
  topPages: Array<{ page: string; views: number }>;
  topFeatures: Array<{ name: string; uses: number; users: number }>;
  locations: Array<{
    country: string;
    countryCode: string | null;
    city: string;
    region: string | null;
    events: number;
    users: number;
    ips: number;
  }>;
  locationsUnknown: { events: number; ips: number };
}

export interface PumpSeriesResponse {
  pumpId: PumpId;
  displayName: string;
  from: string;
  to: string;
  traces: Array<{
    metric: PumpMetric;
    label: string;
    unit: string;
    referenceId: string;
    points: { t: string; v: number | null }[];
  }>;
}

// All requests are same-origin: in production Fastify serves both the SPA
// and /api/*; in dev Vite proxies /api to :3000. So relative paths just work.
async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText} — ${path}`);
  return res.json() as Promise<T>;
}

async function putJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText} — ${path}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch { /* swallow */ }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText} — ${path}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch { /* swallow */ }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

export const api = {
  drive: () => get<DriveInfo>("/api/drive"),
  latest: () => get<LatestResponse>("/api/latest"),
  health: () => get<HealthResponse>("/api/health"),
  series: (opts: {
    ids?: ParameterId[];
    from?: string;
    to?: string;
    /** Chainage window (metres) — alternative to from/to; wins when set. */
    fromChainage?: number;
    toChainage?: number;
    preprocessing: PreprocessingParams;
  }) => {
    const qs = new URLSearchParams();
    if (opts.ids?.length) qs.set("ids", opts.ids.join(","));
    if (opts.from) qs.set("from", opts.from);
    if (opts.to) qs.set("to", opts.to);
    if (opts.fromChainage != null) qs.set("fromChainage", String(opts.fromChainage));
    if (opts.toChainage != null) qs.set("toChainage", String(opts.toChainage));
    qs.set("preprocessing", JSON.stringify(opts.preprocessing));
    return get<SeriesResponse>(`/api/series?${qs.toString()}`);
  },
  secondary: (opts: {
    analysis: SecondaryAnalysis;
    from?: string;
    to?: string;
    fromChainage?: number;
    toChainage?: number;
    preprocessing: PreprocessingParams;
  }) => {
    const qs = new URLSearchParams();
    qs.set("analysis", opts.analysis);
    if (opts.from) qs.set("from", opts.from);
    if (opts.to) qs.set("to", opts.to);
    if (opts.fromChainage != null) qs.set("fromChainage", String(opts.fromChainage));
    if (opts.toChainage != null) qs.set("toChainage", String(opts.toChainage));
    qs.set("preprocessing", JSON.stringify(opts.preprocessing));
    return get<SecondaryResponse>(`/api/secondary?${qs.toString()}`);
  },

  slurrySeries: (opts: { channels: SlurryChannelId[]; from?: string; to?: string }) => {
    const qs = new URLSearchParams();
    qs.set("channels", opts.channels.join(","));
    if (opts.from) qs.set("from", opts.from);
    if (opts.to) qs.set("to", opts.to);
    return get<SlurrySeriesResponse>(`/api/slurry/series?${qs.toString()}`);
  },
  slurrySummary: () => get<SlurrySummaryResponse>("/api/slurry/summary"),
  slurryExportUrl: (opts: { from: string; to: string; channels?: SlurryChannelId[] }): string => {
    const qs = new URLSearchParams({ from: opts.from, to: opts.to });
    if (opts.channels?.length) qs.set("channels", opts.channels.join(","));
    return `/api/slurry/export?${qs.toString()}`;
  },

  exportUrl: (opts: {
    ids: ParameterId[];
    from: string;
    to: string;
    mode: "raw" | "processed";
    preprocessing: PreprocessingParams;
  }): string => {
    const qs = new URLSearchParams();
    qs.set("ids", opts.ids.join(","));
    qs.set("from", opts.from);
    qs.set("to", opts.to);
    qs.set("mode", opts.mode);
    qs.set("preprocessing", JSON.stringify(opts.preprocessing));
    return `/api/export?${qs.toString()}`;
  },


  pumpAvailability: () => get<PumpAvailabilityResponse>("/api/pumps/availability"),
  pumpSeries: (opts: {
    pump: PumpId;
    from?: string;
    to?: string;
    preprocessing: PreprocessingParams;
  }) => {
    const qs = new URLSearchParams();
    qs.set("pump", opts.pump);
    if (opts.from) qs.set("from", opts.from);
    if (opts.to) qs.set("to", opts.to);
    qs.set("preprocessing", JSON.stringify(opts.preprocessing));
    return get<PumpSeriesResponse>(`/api/pumps/series?${qs.toString()}`);
  },

  setPipeSchedule: (schedule: PipeSchedule) =>
    putJson<DriveInfo>("/api/drive/pipe-schedule", schedule),

  savePipeChanges: (changes: PipeChange[], minAdvanceM: number) =>
    putJson<DriveInfo>("/api/drive/pipe-changes", { changes, minAdvanceM }),

  chainageTime: (chainageM: number) =>
    get<{ ts: string | null }>(
      `/api/drive/chainage-time?chainage=${encodeURIComponent(chainageM)}`,
    ),
  lastActiveBeforeChainage: (chainageM: number) =>
    get<{ ts: string | null }>(
      `/api/drive/last-active-before-chainage?chainage=${encodeURIComponent(chainageM)}`,
    ),

  resetPipeSchedule: () =>
    postJson<DriveInfo>("/api/drive/pipe-reset", {}),

  setRamStageCorrection: (cfg: RamStageCorrection) =>
    putJson<DriveInfo>("/api/drive/ram-stage-correction", cfg),

  setSavedPreprocessing: (saved: SavedPreprocessing) =>
    putJson<DriveInfo>("/api/drive/preprocessing", saved),

  lubrication: {
    latest: () => get<LubricationLatest>("/api/lubrication/latest"),
    series: (opts: { from?: string; to?: string }) => {
      const qs = new URLSearchParams();
      if (opts.from) qs.set("from", opts.from);
      if (opts.to) qs.set("to", opts.to);
      return get<{ records: BentoniteRecord[] }>(
        `/api/lubrication/series?${qs.toString()}`,
      );
    },
    perChainage: (opts: { from?: string; to?: string; binM?: number }) => {
      const qs = new URLSearchParams();
      if (opts.from) qs.set("from", opts.from);
      if (opts.to) qs.set("to", opts.to);
      if (opts.binM != null) qs.set("binM", String(opts.binM));
      return get<{ rows: PerChainageRow[] }>(
        `/api/lubrication/per-chainage?${qs.toString()}`,
      );
    },
  },

  resyncReadings: (body?: {
    startDate?: string;
    endDate?: string | null;
  }) =>
    postJson<{ ok: boolean; message: string }>(
      "/api/admin/resync-readings",
      body ?? {},
    ),

  slurryManual: {
    list: (opts: { from?: string; to?: string }) => {
      const qs = new URLSearchParams();
      if (opts.from) qs.set("from", opts.from);
      if (opts.to) qs.set("to", opts.to);
      return get<{ readings: SlurryManualReading[] }>(`/api/slurry/manual?${qs.toString()}`);
    },
    create: (body: SlurryManualCreateBody) =>
      postJson<SlurryManualReading>("/api/slurry/manual", body),
    delete: async (id: number): Promise<{ ok: boolean }> => {
      const res = await fetch(`/api/slurry/manual/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return (await res.json()) as { ok: boolean };
    },
  },

  diary: {
    list: () => get<{ entries: DiaryEntry[] }>("/api/diary"),
    create: (occurredAt: string | null, description: string) =>
      postJson<{ entry: DiaryEntry }>("/api/diary", { occurredAt, description }),
    delete: async (id: number): Promise<{ ok: boolean }> => {
      const res = await fetch(`/api/diary/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return (await res.json()) as { ok: boolean };
    },
    clear: async (): Promise<{ ok: boolean; deleted: number }> => {
      const res = await fetch("/api/diary", { method: "DELETE" });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return (await res.json()) as { ok: boolean; deleted: number };
    },
    exportUrl: () => "/api/diary/export.csv",
  },

  saveDriveSetup: (body: {
    name: string;
    pipeOdMm: number;
    pipeIdMm: number;
    lengthM: number;
    shiftTimezone: string;
    shiftDayStartHour: number;
    shiftNightStartHour: number;
    shiftNightEnabled: boolean;
    inclineConfig: InclineConfig;
  }) => putJson<DriveInfo>("/api/drive/setup", body),

  whoami: () =>
    get<{ authenticated: boolean; gateEnabled: boolean; role: "full" | "slurry" | null }>(
      "/api/whoami",
    ),
  login: (password: string, userId?: string) =>
    postJson<{ ok: boolean }>("/api/login", { password, userId }),
  adminAnalytics: (days: number) =>
    get<AdminAnalytics>(`/api/admin/analytics?days=${days}`),
  logout: () => postJson<{ ok: boolean }>("/api/logout", {}),

  jackingForce: {
    config: () => get<JfConfigResponse>("/api/jacking-force/config"),
    saveConfig: (body: JfConfigBody) =>
      putJson<JfConfigResponse>("/api/jacking-force/config", body),
    predictions: () => get<JfPredictionsResponse>("/api/jacking-force/predictions"),
    recompute: () =>
      postJson<JfPredictionsResponse>("/api/jacking-force/recompute", {}),
  },

  drawing: () => get<DrawingInfo>("/api/drawing"),
  uploadDrawing: async (file: File): Promise<{ ok: boolean; sizeBytes: number; mime: string }> => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch("/api/drawing", { method: "POST", body: fd });
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try {
        const j = await res.json() as { error?: string };
        if (j?.error) msg = j.error;
      } catch { /* swallow */ }
      throw new Error(msg);
    }
    return res.json() as Promise<{ ok: boolean; sizeBytes: number; mime: string }>;
  },
  saveAlignment: (planPolyline: Polyline | null, elevationPolyline: Polyline | null) =>
    putJson<{ ok: boolean }>("/api/drawing/alignment", { planPolyline, elevationPolyline }),

  autoAnalyzeDrawing: (body: DrawingAutoAnalyzeRequest) =>
    postJson<DrawingAutoAnalyzeResponse>("/api/drawing/auto-analyze", body),

  alerts: {
    configs: () => get<{ configs: AlertConfig[] }>("/api/alerts/config"),
    createConfig: (body: AlertCreateBody) =>
      postJson<{ config: AlertConfig }>("/api/alerts/config", body),
    updateConfig: (id: number, body: { enabled?: boolean; threshold?: number; direction?: AlertDirection; cooldownMinutes?: number; hysteresisPct?: number }) =>
      putJson<{ config: AlertConfig }>(`/api/alerts/config/${id}`, body),
    deleteConfig: async (id: number): Promise<{ ok: boolean }> => {
      const res = await fetch(`/api/alerts/config/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return (await res.json()) as { ok: boolean };
    },
    resetConfig: async (id: number): Promise<{ ok: boolean }> => {
      const res = await fetch(`/api/alerts/config/${id}/reset`, { method: "POST" });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return (await res.json()) as { ok: boolean };
    },
    recipients: () => get<{ recipients: AlertRecipient[] }>("/api/alerts/recipients"),
    addRecipient: (body: AlertRecipientCreateBody) =>
      postJson<{ recipient: AlertRecipient }>("/api/alerts/recipients", body),
    deleteRecipient: async (id: number): Promise<{ ok: boolean }> => {
      const res = await fetch(`/api/alerts/recipients/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return (await res.json()) as { ok: boolean };
    },
    events: () => get<{ events: AlertEvent[] }>("/api/alerts/events"),
    status: () => get<AlertsStatusResponse>("/api/alerts/status"),
  },
};
